<!-- Nested tabs for Gopher Buddy Setup -->
<mat-tab-group
  mat-stretch-tabs="false"
  mat-align-tabs="start"
  class="nested-tab-group"
  [selectedIndex]="gopherBuddySubTabIndex"
  (selectedIndexChange)="onGopherBuddySubTabChange($event)"
>
  <mat-tab label="Configure Settings">
    <div class="content-box flex flex-col gap-4">
      <div class="flex flex-row">
        <span class="body-text flex !w-[80%]">The Gopher Buddy service must be set to run under the authority of a Google Workspace for Education user that has a minimum of privileges assigned at the root level on the domain.</span>
        <mat-icon class="icon-help">help_outline</mat-icon>
      </div>
      <span class="body-text-bold-gray">Select the admin user with whose authority you want the Gopher Buddy service to run</span>

      <form [formGroup]="gopherBuddyForm" class="gopher-buddy-form">
        <!-- Radio buttons for admin type -->
        <lib-cdw-radio-group formControlName="adminType" class="form-group-radio">
          <lib-cdw-radio-button class="flex flex-row" value="select" name="adminType">
            <span class="body-text">Super Administrator</span>
          </lib-cdw-radio-button>
            <lib-cdw-radio-button class="flex flex-row" value="manual" name="adminType">
              <span class="body-text">Delegated (role-based) Administrator</span>
            </lib-cdw-radio-button>
        </lib-cdw-radio-group>

        <!-- Admin email autocomplete dropdown -->
        <div class="max-w-[400px]">
          <lib-cdw-autocomplete
            [values]="filteredAdminEmailOptions$"
            [multiSelect]="false"
            [placeholder]="'Select admin email'"
            [label]="'email'"
            formControlName="adminEmail"
            [disableLastPass]="true"
            (textChanged)="onAdminEmailTextChanged($event)"
          ></lib-cdw-autocomplete>
          @if (adminEmailTypedTextError) {
            <span class="block text-red-500 text-xs -mt-[22px] mb-2">
              {{ adminEmailTypedTextError }}
            </span>
          }
        </div>

        <!-- Save button -->
          <div class="flex items-center gap-3">
            <lib-cdw-button
              [customColor]="tertiaryBlueCta"
              (buttonClick)="saveAdminSettings()"
              fontFamily="Roboto, sans-serif"
              [disabled]="isSaving || adminEmailHasTypedText"
            >
              Save admin setting
            </lib-cdw-button>
            @if (isSaving) {
              <lib-cdw-spinner size="small"></lib-cdw-spinner>
            }
          </div>
      </form>
    </div>

    <div class="content-box">
      <div class="section-header-row">
        <span class="body-text-bold-gray">Do you want to allow the Gopher Buddy Extension to notify users when ChromeOS updates are available?</span>
        <mat-icon class="icon-help">help_outline</mat-icon>
      </div>

      <form class="flex flex-col gap-4" [formGroup]="chromeUpdateNotificationForm">
          <!-- Yes/No Slide Toggle -->
          <div class="form-row-toggle flex gap-3">
            <span [ngClass]="chromeUpdateNotificationForm.get('allowNotifications')?.value ? 'body-text-muted' : 'body-text-bold'">No, don't allow</span>
            <lib-cdw-slide-toggle
              formControlName="allowNotifications"
              ariaLabel="Allow ChromeOS update notifications"
              [showCheckmark]="true"
              [trackColor]="toggleTrackBg"
              [thumbColor]="tertiaryBlueCta"
              [thumbUncheckedColor]="primaryWhite"
              [checkmarkColor]="primaryWhite"
              [minusIconColor]="primaryBlack"
            ></lib-cdw-slide-toggle>
            <span [ngClass]="chromeUpdateNotificationForm.get('allowNotifications')?.value ? 'body-text-bold' : 'body-text-muted'">Yes, allow</span>
          </div>

          <!-- Notification Display Checkbox -->
          <div class="form-row-checkbox">
            <lib-cdw-checkbox formControlName="displayNotification" [checkmarkColor]="tertiaryBlueCta" [fillColor]="primaryWhite" [borderColor]="tertiaryBlueCta">
              <span class="body-text">Display a Chrome notification when device OS is</span>
            </lib-cdw-checkbox>
            <lib-cdw-number-input
              formControlName="majorVersionsThreshold"
              class="form-input-number-inline"
              [min]="1"
              [disableLastPass]="true"
            ></lib-cdw-number-input>
            <span class="body-text">or more major versions behind.</span>
          </div>

          <!-- Restrict Major Updates Checkbox -->
          <div class="form-row-checkbox indented">
            <lib-cdw-checkbox formControlName="restrictMajorUpdates" [checkmarkColor]="tertiaryBlueCta" [fillColor]="primaryWhite" [borderColor]="tertiaryBlueCta">
              <span class="body-text">Restrict updates to a </span><strong class="body-text">major</strong><span class="body-text"> Google Chrome version of at most</span>
            </lib-cdw-checkbox>
            <lib-cdw-number-input
              formControlName="restrictMajorVersions"
              class="form-input-number-inline"
              [min]="1"
              [disableLastPass]="true"
            ></lib-cdw-number-input>
          </div>

          <!-- And Minor Versions Checkbox -->
          <div class="form-row-checkbox">
            <lib-cdw-checkbox formControlName="enableMinorRestriction" [checkmarkColor]="tertiaryBlueCta" [fillColor]="primaryWhite" [borderColor]="tertiaryBlueCta">
              <span class="body-text">And</span>
            </lib-cdw-checkbox>
            <lib-cdw-number-input
              formControlName="minorVersionsThreshold"
              class="form-input-number-inline"
              [min]="1"
              [disableLastPass]="true"
            ></lib-cdw-number-input>
            <span class="body-text">or more <strong class="body-text">minor</strong> versions behind.</span>
          </div>

          <!-- Restrict Minor Updates Checkbox -->
          <div class="form-row-checkbox indented">
            <lib-cdw-checkbox formControlName="restrictMinorUpdates" [checkmarkColor]="tertiaryBlueCta" [fillColor]="primaryWhite" [borderColor]="tertiaryBlueCta">
              <span class="body-text">Restrict updates to a <strong>minor</strong> Google Chrome version of at most</span>
            </lib-cdw-checkbox>
            <lib-cdw-number-input
              formControlName="restrictMinorVersions"
              class="form-input-number-inline"
              [min]="1"
              [disableLastPass]="true"
            ></lib-cdw-number-input>
          </div>

          <!-- Reminder Emails Input -->
          <div class="layout-vertical">
            <span class="body-text">Send monthly reminders to check/update the max Gopher Buddy Chrome version to the following email address(es):</span>
            <lib-common-ui-components-text-input
              formControlName="reminderEmails"
              [label]="'Enter comma-separated email(s) to send notification...'"
              [placeholder]="'Enter comma-separated email(s) to send notification...'"
              class="email-text-input max-w-[600px]"
              [disableLastPass]="true"
            ></lib-common-ui-components-text-input>
            @if (chromeUpdateNotificationForm.get('reminderEmails')?.hasError('formatError') &&
                 (chromeUpdateNotificationForm.get('reminderEmails')?.dirty || showEmailValidationError)) {
              <span class="block text-red-500 text-xs -mt-[22px]">
                {{ chromeUpdateNotificationForm.get('reminderEmails')?.getError('formatError').value }}
              </span>
            }
            @if (chromeUpdateNotificationForm.get('reminderEmails')?.hasError('invalidEmails') &&
                 (chromeUpdateNotificationForm.get('reminderEmails')?.dirty || showEmailValidationError)) {
              <span class="block text-red-500 text-xs -mt-[22px]">
                Invalid email(s): {{ chromeUpdateNotificationForm.get('reminderEmails')?.getError('invalidEmails').value.join(', ') }}
              </span>
            }
          </div>
        </form>
    </div>

    <div class="content-box">
      <div class="section-header-row">
        <span class="body-text-bold-gray">Do you want to allow users to access the device info panel from the Gopher Buddy extension?</span>
        <mat-icon class="icon-help">help_outline</mat-icon>
      </div>

      <form [formGroup]="deviceInfoPanelForm">
        <div class="form-row-toggle flex gap-3">
            <span [ngClass]="deviceInfoPanelForm.get('allowDeviceInfoAccess')?.value ? 'body-text-muted' : 'body-text-bold'">No, don't allow</span>
            <lib-cdw-slide-toggle
              formControlName="allowDeviceInfoAccess"
              ariaLabel="Allow device info panel access"
              [showCheckmark]="true"
              [trackColor]="toggleTrackBg"
              [thumbColor]="tertiaryBlueCta"
              [thumbUncheckedColor]="primaryWhite"
              [checkmarkColor]="primaryWhite"
              [minusIconColor]="primaryBlack"
            ></lib-cdw-slide-toggle>
            <span [ngClass]="deviceInfoPanelForm.get('allowDeviceInfoAccess')?.value ? 'body-text-bold' : 'body-text-muted'">Yes, allow</span>
        </div>
        <div *ngIf="deviceInfoPanelForm.get('allowDeviceInfoAccess')?.value" class="picker-section">
          <span class="body-text">Give users under the following OUs access to basic device info via Gopher Buddy.</span>
          <div class="picker-container">
            <lib-cdw-filtered-tree-picker
              floatLabel="always"
              filterPlaceholder="Search OUs"
              [multiSelect]="true"
              formControlName="basicDeviceInfoOus"
              height="400px"
              [dataSource]="ouTreeData"
            ></lib-cdw-filtered-tree-picker>
          </div>

          <span class="body-text">Give users under the following OUs access to full device info via Gopher Buddy.</span>
          <div class="picker-container">
            <lib-cdw-filtered-tree-picker
              floatLabel="always"
              filterPlaceholder="Search OUs"
              [multiSelect]="true"
              formControlName="fullDeviceInfoOus"
              height="400px"
              [dataSource]="ouTreeData"
            ></lib-cdw-filtered-tree-picker>
          </div>
        </div>
      </form>

      <!-- Save Configuration Button -->
        <div class="flex items-center gap-3">
          <lib-cdw-button
            [customColor]="tertiaryBlueCta"
            (buttonClick)="saveConfigurationPreferences()"
            fontFamily="Roboto, sans-serif"
            [disabled]="isSaving"
          >
            Save configuration preferences
          </lib-cdw-button>
          @if (isSaving) {
            <lib-cdw-spinner size="small"></lib-cdw-spinner>
          }
        </div>
    </div>

    <div class="content-box">
      <div class="layout-horizontal-start">
        <img src="images/gopher_buddy_checkmark.png" class="h-full w-auto max-h-12 opacity-85" />
        <div class="flex flex-col gap-3">
          <span class="flex flex-row body-text-bold-gray">Do you want to roll out the Gopher Buddy extension to your devices?</span>
          <div>
            <span class="body-text">Please</span>
            <a [href]="gopherBuddySetupArticleUrl" target="_blank" class="link-support"> see this support article »</a>
          </div>
        </div>
      </div>
    </div>
  </mat-tab>
  <mat-tab label="Reset/Uninstall Settings">
    <div class="content-box flex flex-col gap-3">
      <div class="!w-[90%]">
        <span class="body-text-bold-gray">If you have an expired trial or subscription,</span>
        <span class="body-text"> it's highly recommended that you either </span>
        <a href="#" class="link-support">upgrade to {{ appName }},</a>
        <span class="body-text"> or uninstall the extension. </span>
        <a [href]="gopherBuddySetupArticleUrl" target="_blank" class="link-support font-normal">See this support article on the uninstallation process »</a>
      </div>
      <div class="flex flex-col gap-3 my-2 !w-[90%]">
        <div class="flex flex-row">
          <span class="body-text">Protect student data and provide us with your feedback.</span>
          <mat-icon class="icon-help">help_outline</mat-icon>
        </div>
        <span class="body-text">If you are discontinuing your use of Gopher Buddy, your privacy obligations may necessitate the complete removal of student data from our systems.</span>
      </div>
      <!-- Request permanent data deletion button -->
          <lib-cdw-button
            [customColor]="tertiaryBlueCta"
            (buttonClick)="requestPermanentDeletion()"
            fontFamily="Roboto, sans-serif"
          >
            Request permanent data deletion
          </lib-cdw-button>
    </div>

      <div class="content-box flex flex-col gap-3">
        <div class="!w-[80%]">
          <span class="body-text-bold-gray">You may reset all Gopher Buddy settings for your domain(s) to start anew. </span>
          <span class="body-text">This option is recommended if you are experiencing issues with Gopher Buddy session capture and are collaborating with our support team to investigate and resolve the problem.</span>
        </div>
          <div class="my-2 !w-[80%]">
            <span class="body-text-bold-gray">Warning:</span>
            <span class="body-text"> Resetting will result in the loss of your current settings. It is recommended to take a screenshot of your current settings as a backup before proceeding.</span>
          </div>
        <!-- Request permanent data deletion button -->
            <lib-cdw-button
              [customColor]="tertiaryBlueCta"
              (buttonClick)="resetGBSettings()"
              fontFamily="Roboto, sans-serif"
            >
              Reset Gopher Buddy settings
            </lib-cdw-button>
      </div>
  </mat-tab>
</mat-tab-group>
